import React,{useEffect} from "react"
import PropTypes from "prop-types"
import { Upload } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { Text } from "ui/Typography"
import { colors } from "themes/variables"
import { useFileUpload } from "../hooks/useFileUpload"
import FilePreview from "../components/FilePreview"
import styles from "./index.module.css"

const { Dragger } = Upload

/**
 * UploadFile Component
 * Handles file upload with validation, progress tracking, and error handling
 * Refactored following SOLID principles and code optimization guidelines
 *
 * @component
 * @param {Function} onFileListChange - Optional callback for file list changes
 * @param {Function} handleFileChangeImport - Callback when file is successfully validated
 */


const UploadFile = ({ onFileListChange, handleFileChangeImport, isReportModalOpen }) => {
  const { t } = useTranslation()

  // Use custom hook for upload logic
  const { fileList, setFileList, uploading, progress, uploadComplete, error, setError, beforeUpload, handleChange, handleRemove, customRequest } =
    useFileUpload(handleFileChangeImport, onFileListChange)

  // Clear fileList when ImportReportModal is opened or closed
  useEffect(() => {
    setFileList([])
  }, [isReportModalOpen, setFileList])

  return (
    <div className={styles.uploadContainer}>
      <Dragger
        name="file"
        multiple={false}
        beforeUpload={beforeUpload}
        onChange={handleChange}
        onRemove={handleRemove}
        fileList={fileList}
        showUploadList={false}
        accept=".xlsx"
        customRequest={customRequest}
      >
        {fileList.length === 0 ? (
          <div className="flex-row gap-xs">
            <div className={styles.circle}>
              <Icon name="Upload" color={colors.textPrimary} width={16} height={16} strokeWidth={1.4} />
            </div>
            <div className="align-center justify-center">
              <div className="flex-column align-center">
                <Text variant="link" className="mb-xxs">
                  {t("global.upload")}
                </Text>
                <Text className="flex-1" variant="caption" size="md">
                  {t("clients.only_xlsx_format_supported_with_file_size_limit")}
                </Text>
              </div>
            </div>
          </div>
        ) : (
          <FilePreview
            file={fileList[0]}
            uploading={uploading}
            uploadComplete={uploadComplete}
            progress={progress}
            error={error}
            onRemove={handleRemove}
            onErrorClear={() => setError("")}
          />
        )}
      </Dragger>
    </div>
  )
}


UploadFile.propTypes = {
  onFileListChange: PropTypes.func,
  handleFileChangeImport: PropTypes.func.isRequired,
  isReportModalOpen: PropTypes.bool
}

export default UploadFile
